import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { SiteImport, SitePagesResult } from './knowledgePages'

export type CallMode = 'inbound' | 'outbound'

export interface AIAgent {
  id: number
  name: string
  phone_number_display: string
  language: string
  is_active: boolean
  call_mode?: CallMode
  agent_template?: string
  template_config?: Record<string, unknown>
  google_calendar?: number | null
  microsoft_calendar?: number | null
  square_booking?: number | null
  wix_booking?: number | null
  /** Read only: the Wix calendar is connected and usable. */
  wix_booking_enabled?: boolean
  can_cancel_appointments?: boolean
  can_reschedule_appointments?: boolean
  created_at: string
  voice_id?: string
  phone_number?: number | null
  uses_company_main_number?: boolean
  first_message?: string
  system_prompt?: string
  business_context?: string
  knowledge_web_urls?: string[]
  knowledge_text?: string
  sms_actions?: unknown[]
  transfer_actions?: unknown[]
  notify_admin_actions?: unknown[]
  /** Numéro expéditeur SMS quand l'agent n'a pas de numéro dédié (agents via flow IVR). */
  sms_from_number?: string | null
  /** Activité des 30 derniers jours, fournie par la liste. */
  activity?: { calls_30d: number; minutes_30d: number; last_call_at: string | null } | null
  /** Flux (actifs, branchés) où l'agent répond via un nœud « Vers Agent IA ». */
  answers_in_flows?: string[]
  /** Read only: expressive mode is on (the agent speaks with one of our house voices). */
  mode_expressif?: boolean
  /** Read only: the voice used in expressive mode, even while it is off. */
  voix_expressive?: ExpressiveVoice
}

export interface CreateAIAgentInput {
  name: string
  call_mode: CallMode
  agent_template?: string
  template_config?: Record<string, unknown>
  first_message?: string
  system_prompt?: string
  voice_id?: string
  language?: string
  phone_number_id?: string | number | null
  business_context?: string
  knowledge_web_urls?: string[]
  knowledge_text?: string
  sms_actions?: unknown[]
  transfer_actions?: unknown[]
  notify_admin_actions?: unknown[]
  sms_from_number?: string | null
  /** Merged by the server into the agent's configuration, other keys untouched. */
  mode_expressif?: boolean
  /** Id of one of our house voices; omitted = no change. */
  voix_expressive?: string
}

/** One of our house voices for expressive mode. */
export interface ExpressiveVoice {
  id: string
  prenom: string | null
  genre: 'femme' | 'homme' | null
  /** Every voice speaks French and English; only the accent differs. */
  accent: 'fr' | 'en' | null
  /** Sample served by the dashboard itself (public/voix-expressives/); null = none yet. */
  extrait: string | null
  /** The voice of an agent that never picked one. */
  par_defaut?: boolean
}

interface ExpressiveVoicesResponse {
  voix: ExpressiveVoice[]
  par_defaut: ExpressiveVoice
}

/** GET /api/ai-agents/expressive-voices/ — our house voices and the default one. */
export function useExpressiveVoices() {
  return useQuery({
    queryKey: ['ai-expressive-voices'],
    queryFn: async () => {
      const { data } = await api.get<ExpressiveVoicesResponse>('/api/ai-agents/expressive-voices/')
      return data
    },
    staleTime: 60 * 60_000,
  })
}

export interface ElevenLabsVoice {
  voice_id: string
  name: string
  preview_url?: string | null
  description?: string
  labels?: {
    accent?: string
    gender?: string
    age?: string
    use_case?: string
    language?: string
  }
  category?: string
  /** The default voice of its language (Perle, Sarah). */
  par_defaut?: boolean
  /** Not offered: already set on one of the company's agents, kept so its card shows it. */
  en_place?: boolean
}

interface VoicesResponse {
  voices: ElevenLabsVoice[]
}

/**
 * GET /api/elevenlabs/voices/ — the voices offered to customers (Perle and Sarah first,
 * marked par_defaut), then the voices already set on the company's agents (en_place).
 */
export function useElevenLabsVoices() {
  return useQuery({
    queryKey: ['elevenlabs-voices'],
    queryFn: async () => {
      const { data } = await api.get<VoicesResponse>('/api/elevenlabs/voices/')
      return data.voices ?? []
    },
    staleTime: 5 * 60_000,
  })
}

export interface AIPhoneNumber {
  id: number
  phone_number: string
  assignment_type: string
  is_toll_free: boolean
}

export interface NumeroAvecOccupant {
  id: number
  phone_number: string
  label: string
  assignment_type: string
  /** Ce qui répond aujourd'hui sur ce numéro ; null = rien. */
  answers_today: { kind: 'flow' | 'agent' | 'power_dialer' | 'team'; name: string; agent_id?: number } | null
  available: boolean
  /** Déjà branché sur l'agent en cours. */
  is_current: boolean
}

interface AvailableNumbersResponse {
  phone_numbers: AIPhoneNumber[]
  company_main_number: string | null
  all_numbers?: NumeroAvecOccupant[]
}

/** GET /api/companies/{id}/phone-numbers/available/?agent_id=X — numéros assignables à un AI agent. */
export function useAvailableAIPhoneNumbers(
  companyId: number | undefined,
  agentId?: number,
) {
  return useQuery({
    queryKey: ['ai-available-phone-numbers', companyId, agentId],
    queryFn: async () => {
      const url = agentId
        ? `/api/companies/${companyId}/phone-numbers/available/?agent_id=${agentId}`
        : `/api/companies/${companyId}/phone-numbers/available/`
      const { data } = await api.get<AvailableNumbersResponse>(url)
      return data
    },
    enabled: !!companyId,
    staleTime: 30_000,
  })
}

export interface SmsFromNumberOption {
  phone_number: string
  label: string
  type: string
}

/**
 * GET /api/companies/{companyId}/ai-agents/{agentId}/sms-from-numbers/
 * Liste tous les numéros utilisables comme expéditeur SMS pour cet agent
 * (tous les numéros de la company, peu importe leur assignation).
 */
export function useSmsFromNumbers(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  return useQuery({
    queryKey: ['ai-sms-from-numbers', companyId, agentId],
    queryFn: async () => {
      const { data } = await api.get<{ numbers: SmsFromNumberOption[] }>(
        `/api/companies/${companyId}/ai-agents/${agentId}/sms-from-numbers/`,
      )
      return data.numbers ?? []
    },
    enabled: !!companyId && !!agentId,
    staleTime: 30_000,
  })
}

/** GET /api/companies/{id}/ai-agents/ — liste des agents IA de l'entreprise. */
export function useAIAgents(companyId: number | undefined) {
  return useQuery({
    queryKey: ['ai-agents', companyId],
    queryFn: async () => {
      const { data } = await api.get<AIAgent[]>(
        `/api/companies/${companyId}/ai-agents/`,
      )
      return data
    },
    enabled: !!companyId,
    staleTime: 15_000,
  })
}

/** POST /api/companies/{id}/ai-agents/ — crée un agent IA. */
export function useCreateAIAgent(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateAIAgentInput) => {
      const { data } = await api.post<AIAgent>(
        `/api/companies/${companyId}/ai-agents/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents', companyId] })
    },
  })
}

/** PATCH /api/companies/{id}/ai-agents/{agentId}/ — patch partiel. */
export function useUpdateAIAgent(companyId: number | undefined, agentId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<CreateAIAgentInput & { is_active: boolean }>) => {
      const { data } = await api.patch<AIAgent>(
        `/api/companies/${companyId}/ai-agents/${agentId}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents', companyId] })
      // Saving a new greeting text or voice starts its preparation on the server.
      queryClient.invalidateQueries({ queryKey: ['ai-agent-greeting', companyId, agentId] })
    },
  })
}

// =============================================================================
// Greeting to listen to (the recording played on pickup for GPT-Live agents,
// a preview in the same voice for the other engines)
// =============================================================================

export type GreetingState = 'pret' | 'preparation' | 'echec' | 'absent' | 'aucun'

export interface GreetingStatus {
  etat: GreetingState
  /** Length in seconds, once ready. */
  duree?: number
  /** live | openai | deepgram | elevenlabs */
  moteur?: string
  /** Changes with the greeting text and voice: keys the downloaded audio. */
  version?: string
  /** Why there is nothing to play: no greeting, or one that changes with each call. */
  raison?: 'vide' | 'variable' | 'sans_voix'
}

const greetingUrl = (companyId: number, agentId: number) =>
  `/api/companies/${companyId}/ai-agents/${agentId}/greeting/`

/** GET greeting/: state of the agent's greeting; polled while it is being prepared. */
export function useGreetingStatus(companyId: number | undefined, agentId: number | undefined) {
  return useQuery({
    queryKey: ['ai-agent-greeting', companyId, agentId],
    queryFn: async () => {
      const { data } = await api.get<GreetingStatus>(greetingUrl(companyId!, agentId!))
      return data
    },
    enabled: !!companyId && !!agentId,
    refetchInterval: (query) => (query.state.data?.etat === 'preparation' ? 2000 : false),
    staleTime: 0,
  })
}

/** POST greeting/prepare/: "Try again", or a greeting never prepared yet. */
export function usePrepareGreeting(companyId: number | undefined, agentId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<GreetingStatus>(`${greetingUrl(companyId!, agentId!)}prepare/`)
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['ai-agent-greeting', companyId, agentId], data)
    },
  })
}

/** GET greeting/audio/: the audio itself, fetched with the session (no token in the URL). */
export async function fetchGreetingAudio(companyId: number, agentId: number): Promise<Blob> {
  const { data } = await api.get<Blob>(`${greetingUrl(companyId, agentId)}audio/`, {
    responseType: 'blob',
  })
  return data
}

// =============================================================================
// Appels d'un AI Agent (historique + audio)
// =============================================================================

export interface AICall {
  id: number
  call_sid: string
  from_number: string
  to_number: string
  /** The contact's name when the customer's number is in the address book. */
  contact_name?: string | null
  direction: 'inbound' | 'outbound' | string
  duration: number
  status: string
  transcript: string
  conversation_summary: string
  audio_url: string | null
  started_at: string
  ended_at: string
  elevenlabs_metadata: unknown
}

/** GET /api/companies/{id}/ai-agents/{agentId}/calls/ — historique des appels. */
export function useAIAgentCalls(
  companyId: number | undefined,
  agentId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['ai-agent-calls', companyId, agentId],
    queryFn: async () => {
      const { data } = await api.get<AICall[]>(
        `/api/companies/${companyId}/ai-agents/${agentId}/calls/`,
      )
      return data
    },
    enabled: enabled && !!companyId && !!agentId,
    staleTime: 15_000,
  })
}

/** GET /api/ai-agents/calls/{callId}/audio/ — récupère l'audio en blob via Axios (auth inclus). */
export function useAICallAudio(callId: number | undefined) {
  return useQuery({
    queryKey: ['ai-call-audio', callId],
    queryFn: async () => {
      const { data } = await api.get<Blob>(
        `/api/ai-agents/calls/${callId}/audio/`,
        { responseType: 'blob' },
      )
      return URL.createObjectURL(data)
    },
    enabled: !!callId,
    staleTime: 60_000,
    // Pas de garbage GC automatique côté React Query — le caller révoque l'URL via useEffect cleanup.
  })
}

// =============================================================================
// « Quand votre agent doit-il répondre ? » — branchement guidé
// =============================================================================

export type AnswerMode = 'always' | 'after_hours' | 'overflow'

export interface AnswerModeResult {
  mode: AnswerMode
  phone_number: string
  flow_id?: number
  flow_name?: string
}

/**
 * POST /api/companies/{id}/ai-agents/{agentId}/answer-mode/
 * Traduit la réponse à la question métier en configuration réelle
 * (assignation directe ou flux IVR généré). Erreurs métier notables :
 * team_required, team_choice_required (+teams), number_taken_by_agent,
 * no_main_number, phone_number_required, flow_will_be_replaced
 * (+flow_name — renvoyer replaceFlow: true après confirmation).
 */
export function useApplyAnswerMode(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      agentId,
      mode,
      phoneNumberId,
      teamId,
      userProfileId,
      ringTimeout,
      replaceFlow,
    }: {
      agentId: number
      mode: AnswerMode
      phoneNumberId: number | 'main'
      teamId?: number
      userProfileId?: number
      ringTimeout?: number
      replaceFlow?: boolean
    }) => {
      const { data } = await api.post<AnswerModeResult>(
        `/api/companies/${companyId}/ai-agents/${agentId}/answer-mode/`,
        {
          mode,
          phone_number_id: phoneNumberId,
          ...(teamId ? { team_id: teamId } : {}),
          ...(userProfileId ? { user_profile_id: userProfileId } : {}),
          ...(ringTimeout ? { ring_timeout: ringTimeout } : {}),
          ...(replaceFlow ? { replace_flow: true } : {}),
        },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents', companyId] })
      queryClient.invalidateQueries({ queryKey: ['ai-available-phone-numbers'] })
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
    },
  })
}

// =============================================================================
// Missions d'agent IA
// =============================================================================

export interface MissionCollectField {
  name: string
  type?: 'text' | 'number' | 'boolean'
  required?: boolean
  options?: string[]
}

export type MissionKind =
  | 'take_message'
  | 'after_hours'
  | 'book_appointment'
  | 'confirm_appointment'
  | 'custom'

export interface AIAgentMission {
  id: number
  /** Identité stable, fixée au seed et non modifiable — `name` est un simple libellé. */
  kind: MissionKind
  name: string
  instructions: string
  /** Ouverture propre à la mission — remplace le premier message de
   *  l'agent pour les appels qui la portent ('' = ouverture de l'agent). */
  first_message: string
  variables: string[]
  collect_fields: MissionCollectField[]
  allowed_tools: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export type MissionInput = Omit<AIAgentMission, 'id' | 'created_at' | 'updated_at'>

/** GET /api/companies/{id}/ai-agents/{agentId}/missions/ */
export function useAIAgentMissions(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  return useQuery({
    queryKey: ['ai-agent-missions', companyId, agentId],
    queryFn: async () => {
      const { data } = await api.get<AIAgentMission[]>(
        `/api/companies/${companyId}/ai-agents/${agentId}/missions/`,
      )
      return data
    },
    enabled: !!companyId && !!agentId,
    staleTime: 15_000,
  })
}

/** POST /api/companies/{id}/ai-agents/{agentId}/missions/ */
export function useCreateMission(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<MissionInput> & { name: string; instructions: string }) => {
      const { data } = await api.post<AIAgentMission>(
        `/api/companies/${companyId}/ai-agents/${agentId}/missions/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-missions', companyId, agentId] })
    },
  })
}

/** PATCH /api/companies/{id}/ai-agents/{agentId}/missions/{missionId}/ */
export function useUpdateMission(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      missionId,
      ...input
    }: Partial<MissionInput> & { missionId: number }) => {
      const { data } = await api.patch<AIAgentMission>(
        `/api/companies/${companyId}/ai-agents/${agentId}/missions/${missionId}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-missions', companyId, agentId] })
    },
  })
}

/** DELETE /api/companies/{id}/ai-agents/{agentId}/missions/{missionId}/ */
export function useDeleteMission(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (missionId: number) => {
      await api.delete(
        `/api/companies/${companyId}/ai-agents/${agentId}/missions/${missionId}/`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-missions', companyId, agentId] })
    },
  })
}

// =============================================================================
// Appel de test « Appelez-moi »
// =============================================================================

export interface TestCallStartResponse {
  call_id: number
  call_sid: string
  status: string
  to_number: string
  from_number: string
}

/**
 * POST /api/companies/{id}/ai-agents/{agentId}/test-call/
 * L'agent appelle le numéro fourni (défaut backend : téléphone du profil).
 * Erreurs métier : agent_inactive, agent_not_synced, no_phone_number,
 * invalid_phone_number, no_from_number, test_call_in_progress,
 * service_suspended, payment_suspended.
 */
export function useStartTestCall(companyId: number | undefined) {
  return useMutation({
    mutationFn: async ({
      agentId,
      phoneNumber,
      missionId,
      missionContext,
    }: {
      agentId: number
      phoneNumber?: string
      missionId?: number
      /** Précision de branche, en langage naturel (cf. nœud « Agent IA »). */
      missionContext?: string
    }) => {
      const { data } = await api.post<TestCallStartResponse>(
        `/api/companies/${companyId}/ai-agents/${agentId}/test-call/`,
        {
          ...(phoneNumber ? { phone_number: phoneNumber } : {}),
          ...(missionId ? { mission_id: missionId } : {}),
          ...(missionContext ? { mission_context: missionContext } : {}),
        },
      )
      return data
    },
  })
}

/**
 * GET /api/companies/{id}/ai-agents/{agentId}/test-call/{callId}/
 * Suivi du test : statut live puis transcript/résumé (qui peuvent arriver
 * quelques secondes APRÈS le statut completed — polling à poursuivre).
 */
export function useTestCallStatus(
  companyId: number | undefined,
  agentId: number | undefined,
  callId: number | undefined,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: ['ai-test-call', companyId, agentId, callId],
    queryFn: async () => {
      const { data } = await api.get<AICall>(
        `/api/companies/${companyId}/ai-agents/${agentId}/test-call/${callId}/`,
      )
      return data
    },
    enabled: !!companyId && !!agentId && !!callId,
    refetchInterval: options?.refetchInterval ?? 2000,
    staleTime: 0,
  })
}

/** DELETE /api/companies/{id}/ai-agents/{agentId}/ — supprime un agent IA. */
export function useDeleteAIAgent(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (agentId: number) => {
      await api.delete(`/api/companies/${companyId}/ai-agents/${agentId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents', companyId] })
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
    },
  })
}

// ─── Historique des modifications ────────────────────────────────────────────

export interface AIAgentRevision {
  id: number
  created_at: string
  source: 'dashboard' | 'api' | 'restore'
  author: string
  changed_fields: string[]
  before: Record<string, unknown>
  after: Record<string, unknown>
}

/** GET /api/companies/{id}/ai-agents/{agentId}/revisions/ */
export function useAIAgentRevisions(companyId: number | undefined, agentId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['ai-agent-revisions', companyId, agentId],
    queryFn: async () => (await api.get<AIAgentRevision[]>(`/api/companies/${companyId}/ai-agents/${agentId}/revisions/`)).data,
    enabled: enabled && !!companyId && !!agentId,
  })
}

/** POST .../revisions/{revisionId}/restore/ : remet l'état d'avant cette modification. */
export function useRestoreAIAgentRevision(companyId: number | undefined, agentId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (revisionId: number) =>
      (await api.post(`/api/companies/${companyId}/ai-agents/${agentId}/revisions/${revisionId}/restore/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-agent-revisions', companyId, agentId] })
      qc.invalidateQueries({ queryKey: ['ai-agents', companyId] })
    },
  })
}

// =============================================================================
// Creation assistant of an inbound agent: starting point, site, pages, then the
// import page by page; the agent is created at the end, server side.
// =============================================================================

/** The templates after the law firm one: a block of instructions and a notify action each. */
export type AgentTemplateCode = 'rendez_vous' | 'clinique' | 'domicile' | 'immobilier' | 'apres_heures' | 'prospects'

export type AssistantModel = 'vide' | 'reception' | 'cabinet' | AgentTemplateCode

export interface AssistantStartInput {
  modele: AssistantModel
  /** Law firm only: the areas in both languages, the agent keeps its own. */
  domaines?: { fr: string[]; en: string[] }
  pages: Array<{ url: string; titre: string }>
}

const assistantBase = (companyId: number) => `/api/companies/${companyId}/ai-agents/assistant`

/** POST assistant/pages/ — the useful pages of a site, before the agent exists. */
export function useFindAssistantPages(companyId: number | undefined) {
  return useMutation({
    mutationFn: async (url: string) => {
      const { data } = await api.post<SitePagesResult>(`${assistantBase(companyId!)}/pages/`, { url }, { timeout: 120_000 })
      return data
    },
  })
}

/** POST assistant/ — imports the pages (none when the site was skipped), then creates the agent. */
export function useStartAssistant(companyId: number | undefined) {
  return useMutation({
    mutationFn: async (input: AssistantStartInput) => {
      const { data } = await api.post<{ import_id: string }>(`${assistantBase(companyId!)}/`, input)
      return data.import_id
    },
  })
}

/** GET assistant/<id>/ — polled every 2 s until the agent exists or the creation failed. */
export function useAssistantProgress(companyId: number | undefined, importId: string | null) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['ai-agent-assistant', companyId, importId],
    queryFn: async () => {
      const { data } = await api.get<SiteImport>(`${assistantBase(companyId!)}/${importId}/`)
      if (data.statut === 'termine') void queryClient.invalidateQueries({ queryKey: ['ai-agents', companyId] })
      return data
    },
    enabled: !!companyId && !!importId,
    refetchInterval: (query) => {
      const statut = query.state.data?.statut
      return statut === 'termine' || statut === 'echec' || query.state.error ? false : 2_000
    },
    retry: false,
  })
}
